/**
 * The photo picker: a job's photos inside a chat reply, and a way to put some
 * of them on the job report without opening the job
 * (docs/intelligent-chat-answers-plan.md, "Photo picker").
 *
 * 🚨 Photos have NO captions and NO tags. The `job-photos` bucket is a folder,
 * not a table, so the only facts a photo carries are when it was added and,
 * on a site job, which plot it was tagged to. A tile is labelled with those and
 * nothing else, and the model is told on the tool result that it cannot see
 * what any photo shows — "the new boiler" is a caption nobody wrote.
 *
 * 🚨 The block stores PATHS, never URLs. Blocks are persisted in
 * `ai_messages.ui_blocks` and redrawn a month later; the component builds each
 * tile's address from the path at render (`jobPhotoThumbUrl`, an authenticated
 * app route), so the gate is applied to whoever is looking now.
 *
 * 🚨 Adding photos MERGES with what the report already carries. The PATCH on
 * `/api/job-sheets/[id]` replaces `photoPaths` whole, so sending only the new
 * picks would silently take off every photo the engineer had already chosen —
 * the whole-array REPLACE trap from #28. `mergeReportPhotos` is the one place
 * that joins them, and the component reads the sheet fresh before it calls it.
 *
 * Pure and directive-free (anti-pattern #18): the panel, the Mucka Bar and the
 * oversight grader all import it.
 */

/**
 * The most photos one report carries. Mirrors `MAX_REPORT_PHOTOS` in
 * `src/app/api/job-sheets/[id]/route.ts`, which is not exported from a route
 * file; `photo-picker.test.ts` reads that file and fails if the two differ.
 */
export const REPORT_PHOTO_MAX = 12;

/** Tiles shown in the bubble. The rest are counted, never dropped silently. */
export const PHOTO_PICKER_MAX = 12;

/** What `storage.list` is asked for (`loadJobPhotos`). A listing this long may
 *  have stopped short, so the count past it is a floor, not a total. */
export const PHOTO_LISTING_LIMIT = 100;

export type PhotoWhen = 'before' | 'after';

/** One picked photo as the job sheet stores it (`JobSheetPickedPhoto`). */
export interface ReportPhoto {
  path: string;
  when: PhotoWhen;
}

export interface PhotoPickerPhoto {
  /** Storage path in `job-photos`: `{accountId}/{jobId}/{file}`. */
  path: string;
  /** Short tile label, London time, built server-side: "09:14", "Wed 09:14", "8 Oct". */
  label: string;
  /** Spoken label: "Added Wednesday 8 October at 09:14". */
  aria: string;
  /** "Plot 4 · First fix" on a site job's tagged photo, else null. */
  plot: string | null;
}

/**
 * Where picked photos would go.
 *
 * `open` — a draft capture that prints photos. `none` — no draft and nothing
 * issued. `issued` — the paperwork is finished and frozen. `no-photos` — a
 * draft exists but no document it will produce carries photos.
 */
export type PhotoPickerTarget =
  | {
    state: 'open';
    sheetId: string;
    /** What the photos land on, in the words the button uses. */
    document: 'report' | 'job sheet';
    /** Already on it when the block was built. Display only: an add re-reads. */
    alreadyOn: ReportPhoto[];
  }
  | { state: 'none' }
  | { state: 'issued' }
  | { state: 'no-photos' };

export interface PhotoPickerBlock {
  kind: 'photo-picker';
  jobId: string;
  /** `/jobs/<id>` */
  jobPath: string;
  photos: PhotoPickerPhoto[];
  /** Photos on the job beyond the ones shown. */
  more: number;
  /** True when the listing hit its limit, so `more` is "at least". */
  moreIsFloor: boolean;
  /** Videos on the job. Listed by the folder, never offered: a report is a PDF. */
  videos: number;
  target: PhotoPickerTarget;
}

/** One object from the job's photo listing (`loadJobPhotos`), reduced. */
export interface PhotoCandidate {
  path: string;
  /** ISO timestamp the object was stored, or null. */
  createdAt: string | null;
  kind: 'image' | 'video';
  plot?: { plotLabel: string | null; stageName: string | null } | null;
}

const LONDON = 'Europe/London';

function fmt(d: Date, options: Intl.DateTimeFormatOptions): string {
  // Built on the server and stored, so hydration never sees it; the Sept/Sep
  // ICU split (#41) is pinned anyway so a test run and a phone agree.
  return new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, ...options }).format(d).replace(/\bSept\b/, 'Sep');
}

function londonDay(d: Date): string {
  return fmt(d, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function plotLabel(plot: PhotoCandidate['plot']): string | null {
  if (!plot) return null;
  const parts = [plot.plotLabel, plot.stageName].filter((p): p is string => !!p && !!p.trim());
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The tiles' labels. One day: the time alone. Within a week: weekday and time.
 * Longer: the date, because "Wed" would name two different Wednesdays.
 */
function labelsFor(photos: readonly PhotoCandidate[]): Array<{ label: string; aria: string }> {
  const dates = photos.map(p => (p.createdAt ? new Date(p.createdAt) : null));
  const known = dates.filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));
  const days = new Set(known.map(londonDay));
  const span = known.length
    ? (Math.max(...known.map(d => d.getTime())) - Math.min(...known.map(d => d.getTime()))) / 86_400_000
    : 0;
  return dates.map(d => {
    if (!d || Number.isNaN(d.getTime())) return { label: 'Photo', aria: 'Photo, no date recorded' };
    const time = fmt(d, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    const aria = `Added ${fmt(d, { weekday: 'long', day: 'numeric', month: 'long' })} at ${time}`;
    if (days.size <= 1) return { label: time, aria };
    if (span < 6) return { label: `${fmt(d, { weekday: 'short' })} ${time}`, aria };
    return { label: fmt(d, { day: 'numeric', month: 'short' }), aria };
  });
}

/**
 * The picker, or null when the job has no photos to show.
 *
 * Shows the newest `PHOTO_PICKER_MAX`, oldest first within them, so a strip
 * reads in the order the visit happened. Videos are counted, not offered.
 */
export function photoPickerBlock(args: {
  jobId: string;
  listing: readonly PhotoCandidate[];
  target: PhotoPickerTarget;
}): PhotoPickerBlock | null {
  const { jobId, listing, target } = args;
  const images = listing.filter(p => p.kind === 'image');
  if (images.length === 0) return null;
  const time = (p: PhotoCandidate) => (p.createdAt ? Date.parse(p.createdAt) || 0 : 0);
  // Path breaks a tie, so the same listing always shows the same twelve (#27).
  const newestFirst = images.slice().sort((a, b) => time(b) - time(a) || a.path.localeCompare(b.path));
  const shown = newestFirst.slice(0, PHOTO_PICKER_MAX).reverse();
  const labels = labelsFor(shown);
  return {
    kind: 'photo-picker',
    jobId,
    jobPath: `/jobs/${jobId}`,
    photos: shown.map((p, i) => ({ path: p.path, label: labels[i].label, aria: labels[i].aria, plot: plotLabel(p.plot) })),
    more: images.length - shown.length,
    moreIsFloor: listing.length >= PHOTO_LISTING_LIMIT,
    videos: listing.length - images.length,
    target,
  };
}

/**
 * What the job sheet already holds, in the PATCH's shape.
 *
 * A bare string is a pick made before the before/after strips existed, and
 * reads as `after` — the same rule as `resolvePickedPhotos`, so the merge
 * cannot disagree with the document about which strip an old pick is in.
 */
export function normaliseReportPhotos(raw: unknown): ReportPhoto[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: ReportPhoto[] = [];
  for (const entry of raw) {
    const path = typeof entry === 'string' ? entry : (entry as { path?: unknown } | null)?.path;
    if (typeof path !== 'string' || !path || seen.has(path)) continue;
    const when = typeof entry === 'string' ? 'after' : (entry as { when?: unknown }).when === 'before' ? 'before' : 'after';
    seen.add(path);
    out.push({ path, when });
  }
  return out;
}

export type MergeResult =
  | { ok: true; photos: ReportPhoto[]; added: number }
  | { ok: false; message: string };

/**
 * The report's photos with the new picks added after them.
 *
 * Existing picks keep their place and their strip; a photo already on the
 * report is not added twice. Over the cap, nothing is changed and the sentence
 * says how many will fit — dropping the oldest to make room would rearrange
 * what somebody chose without showing it.
 */
export function mergeReportPhotos(
  current: readonly ReportPhoto[],
  picked: readonly string[],
  when: PhotoWhen,
  document: 'report' | 'job sheet' = 'report',
): MergeResult {
  const have = new Set(current.map(p => p.path));
  const fresh = [...new Set(picked)].filter(p => !have.has(p));
  if (current.length + fresh.length > REPORT_PHOTO_MAX) {
    const room = Math.max(0, REPORT_PHOTO_MAX - current.length);
    return {
      ok: false,
      message: room === 0
        ? `The ${document} already has ${REPORT_PHOTO_MAX} photos, which is as many as it takes. Take one off on the job to make room.`
        : `The ${document} takes up to ${REPORT_PHOTO_MAX} photos and already has ${current.length}, so pick ${room} or fewer.`,
    };
  }
  return { ok: true, photos: [...current, ...fresh.map(path => ({ path, when }))], added: fresh.length };
}

/** "Add 3 photos to the report" */
export function addLabel(count: number, document: 'report' | 'job sheet'): string {
  return `Add ${count} photo${count === 1 ? '' : 's'} to the ${document}`;
}

/** The sentence appended to `list_job_documents`' result when the picker is on screen. */
export function photoPickerResultNote(block: PhotoPickerBlock): string {
  const total = block.photos.length + block.more;
  const shown = block.more > 0 ? `the newest ${block.photos.length} of ${block.moreIsFloor ? 'at least ' : ''}${total} photos` : `${total} photo${total === 1 ? '' : 's'}`;
  const where = block.target.state === 'open'
    ? ` The user can tap photos there to add them to the job's ${block.target.document}; nothing reaches the customer from it.`
    : '';
  return `This job's ${shown} are on screen as a picker.${where} `
    + 'You cannot see what any photo shows and they have no captions, so never describe one or say what it is of; do not list them.';
}

/**
 * One line for the oversight transcript: counts and state, nothing a customer
 * could be identified by.
 */
export function summarisePhotoPickerBlock(block: PhotoPickerBlock): string {
  const total = block.photos.length + block.more;
  const count = `${block.moreIsFloor ? 'at least ' : ''}${total} photo${total === 1 ? '' : 's'}`;
  const shown = block.more > 0 ? `, ${block.photos.length} shown` : '';
  const target = block.target.state === 'open'
    ? `can add to the ${block.target.document} (${block.target.alreadyOn.length} on it)`
    : block.target.state === 'issued'
      ? 'paperwork already issued'
      : block.target.state === 'no-photos'
        ? 'paperwork carries no photos'
        : 'no job report yet';
  return `[photo-picker: ${count}${shown}, ${target}]`;
}
